// frontend/src/components/documents/ClientDocumentsTab.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Send as SendIcon,
  Build as BuildIcon,
  AssignmentReturn as ReturnIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import Anexo1Modal from './Anexo1Modal';
import Anexo2Modal from './Anexo2Modal';

const ClientDocumentsTab = ({ clientId, client }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modales
  const [anexo1Open, setAnexo1Open] = useState(false);
  const [anexo2Open, setAnexo2Open] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchDocuments();
    }
  }, [clientId]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/documents/client/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar historial de documentos');
      const data = await res.json();
      setDocuments(data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPdf = (docId, format = 'A4') => {
    const token = localStorage.getItem('token');
    window.open(`/api/documents/${docId}/pdf?format=${format}&token=${token}`, '_blank');
  };

  const handleSendWhatsapp = async (doc) => {
    const rawPhone = client?.phone || doc?.document_data?.client?.phone;
    if (!rawPhone) {
      alert('El cliente no tiene un número de teléfono registrado.');
      return;
    }

    const cleanPhone = String(rawPhone).replace(/\D/g, '');
    const docData = doc.document_data || {};

    const fullName = 
      (client?.first_name || client?.last_name)
        ? `${client.first_name || ''} ${client.last_name || ''}`.trim()
        : docData.client?.fullName 
        || (docData.client?.firstName ? `${docData.client.firstName} ${docData.client.lastName || ''}`.trim() : 'Abonado');

    const planName = docData.service?.planName || docData.serviceData?.planName || client?.plan_name || 'Plan de Internet';
    const price = docData.service?.price || docData.serviceData?.price || client?.plan_price || '0.00';
    const paymentDay = docData.client?.paymentDay || client?.payment_day || 5;
    const ipAddress = docData.client?.ipAddress || client?.ip_address || 'Asignada';
    const techName = doc.technician_name || doc.generator_name || 'Técnico YinoWISP';
    const formattedDate = new Date(doc.generated_at).toLocaleDateString('es-PE');

    let msg = '';

    if (doc.document_type === 'INSTALLATION_ANEXO1') {
      msg = 
        `Estimado(a) *${fullName}*, le enviamos la Ficha de Instalación y Entrega de Equipos (*Anexo 1*): *${doc.document_number}*.\n\n` +
        `📌 *Plan*: ${planName}\n` +
        `🔧 *Técnico Emisor*: ${techName}\n` +
        `📆 *Fecha de Instalación*: ${formattedDate}\n` +
        `🌐 *Dirección IP*: ${ipAddress}\n\n` +
        `📱 *Puede consultar sus equipos e información en el Portal del Cliente*:\nhttps://cliente.yinowisp.com\n\n` +
        `¡Gracias por confiar en YinoWISP!`;
    } else if (doc.document_type === 'RETURN_ANEXO2') {
      msg = 
        `Estimado(a) *${fullName}*, le enviamos la Ficha de Devolución de Equipos (*Anexo 2*): *${doc.document_number}*.\n\n` +
        `📋 *Motivo*: ${docData.reason || 'Baja del Servicio'}\n` +
        `🛠️ *Técnico Recibidor*: ${techName}\n` +
        `📆 *Fecha de Devolución*: ${formattedDate}\n\n` +
        `¡Gracias por su preferencia con YinoWISP!`;
    } else {
      // Contrato WISP / CONTRACT_OSIPTEL
      msg = 
        `Estimado(a) *${fullName}*, le enviamos la información de su contrato del Servicio de Internet YinoWISP: *${doc.document_number}*.\n\n` +
        `📌 *Plan Contratado*: ${planName}\n` +
        `💰 *Renta Mensual*: S/ ${price}\n` +
        `📅 *Día Límite de Pago*: Día ${paymentDay} de cada mes\n` +
        `🌐 *Dirección IP*: ${ipAddress}\n` +
        `📆 *Fecha de Emisión*: ${formattedDate}\n\n` +
        `📱 *Puede consultar y descargar su contrato completo ingresando al Portal del Cliente*:\nhttps://cliente.yinowisp.com\n\n` +
        `¡Gracias por confiar en YinoWISP!`;
    }

    try {
      const res = await fetch('/api/whatsapp/send-custom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          clientId,
          number: cleanPhone,
          message: msg
        })
      });
      if (res.ok) {
        alert('✅ Documento notificado exitosamente por WhatsApp.');
      } else {
        window.open(`https://wa.me/51${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
      }
    } catch (err) {
      window.open(`https://wa.me/51${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" /> Documentos Contractuales y Fichas Operativas
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Historial de Contratos, Fichas de Instalación (Anexo 1) y Devoluciones (Anexo 2)
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BuildIcon />}
            onClick={() => setAnexo1Open(true)}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
          >
            + Generar Ficha de Instalación (Anexo 1)
          </Button>

          <Button
            variant="contained"
            color="error"
            startIcon={<ReturnIcon />}
            onClick={() => setAnexo2Open(true)}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
          >
            + Generar Ficha de Devolución (Anexo 2)
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: '12px' }}>
          <Typography variant="body1" color="textSecondary" fontWeight={600}>
            No se han registrado fichas operativas ni contratos individuales adicionales para este cliente.
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
            Haga clic en los botones superiores para emitir el Anexo 1 (Instalación) o Anexo 2 (Devolución).
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Tipo de Documento</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>N.° Ficha</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Fecha Emisión</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Técnico / Emisor</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {doc.document_type === 'INSTALLATION_ANEXO1' ? (
                        <Chip label="Anexo 1: Instalación" color="primary" size="small" sx={{ fontWeight: 700 }} />
                      ) : doc.document_type === 'RETURN_ANEXO2' ? (
                        <Chip label="Anexo 2: Devolución" color="error" size="small" sx={{ fontWeight: 700 }} />
                      ) : (
                        <Chip label="Contrato WISP" color="secondary" size="small" sx={{ fontWeight: 700 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontFamily: 'monospace' }}>
                    {doc.document_type === 'CONTRACT_OSIPTEL' && (doc.client_dni || client?.dni)
                      ? `CTR-2026-${doc.client_dni || client?.dni}` 
                      : doc.document_number}
                  </TableCell>
                  <TableCell>
                    {new Date(doc.generated_at).toLocaleString('es-PE')}
                  </TableCell>
                  <TableCell>
                    {doc.technician_name || doc.generator_name || 'YinoWISP Admin'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={doc.status === 'completed' ? 'Completado' : doc.status === 'active' ? 'Activo' : 'Registrado'} 
                      color={doc.status === 'completed' ? 'success' : 'info'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell align="right">
                    {doc.document_type === 'CONTRACT_OSIPTEL' && (
                      <Tooltip title="Descargar Contrato Completo 34 Cláusulas (PDF)">
                        <IconButton size="small" color="secondary" onClick={() => window.open(`/api/documents/${doc.id}/full-pdf?token=${localStorage.getItem('token')}`, '_blank')}>
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Descargar PDF A4">
                      <IconButton size="small" color="primary" onClick={() => handleOpenPdf(doc.id, 'A4')}>
                        <PdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Imprimir Talonario A5">
                      <IconButton size="small" color="info" onClick={() => handleOpenPdf(doc.id, 'A5')}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Enviar por WhatsApp">
                      <IconButton size="small" color="success" onClick={() => handleSendWhatsapp(doc)}>
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal Anexo 1 */}
      <Anexo1Modal
        open={anexo1Open}
        onClose={() => setAnexo1Open(false)}
        clientId={clientId}
        client={client}
        onSuccess={() => {
          setAnexo1Open(false);
          fetchDocuments();
        }}
      />

      {/* Modal Anexo 2 */}
      <Anexo2Modal
        open={anexo2Open}
        onClose={() => setAnexo2Open(false)}
        clientId={clientId}
        client={client}
        onSuccess={() => {
          setAnexo2Open(false);
          fetchDocuments();
        }}
      />
    </Box>
  );
};

export default ClientDocumentsTab;
